import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, ExternalLink, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return s; } };

const STATUS = {
  sent: { label: "Unpaid", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-rose-50 text-rose-600 border-rose-200" },
};

export default function MyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my-invoices").then((r) => setInvoices(r.data)).catch(() => toast.error("Failed to load your invoices")).finally(() => setLoading(false));
  }, []);

  const viewPdf = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}/pdf`);
      if (!data.pdf) return toast.error("Invoice not available");
      const blob = await (await fetch(data.pdf)).blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open invoice"); }
  };

  return (
    <div className="max-w-3xl" data-testid="my-invoices-page">
      <h1 className="text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2">
        <FileText className="h-7 w-7 text-emerald-600" /> My Invoices
      </h1>
      <p className="mt-2 text-sm text-slate-500">Invoices sent to you, with their payment status.</p>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : invoices.length === 0 ? (
        <div className="mt-10 text-sm text-slate-400 border border-dashed border-slate-300 rounded-xl p-8 text-center" data-testid="my-invoices-empty">
          You have no invoices yet.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {invoices.map((inv) => {
            const st = STATUS[inv.status] || { label: inv.status, cls: "bg-slate-50 text-slate-500 border-slate-200" };
            return (
              <div key={inv.id} data-testid="my-invoice-card" className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${st.cls}`} data-testid="my-invoice-status">{st.label}</span>
                    <span className="text-sm text-slate-500">{fmtDate(inv.created_at)}</span>
                  </div>
                  <span className="font-mono font-semibold text-slate-900">{gbp(inv.total_gbp)} <span className="text-xs text-slate-400">({inr(inv.total_inr)})</span></span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{inv.property_name}</p>
                <div className="mt-3 divide-y divide-slate-100">
                  {inv.lines.map((l, i) => (
                    <div key={i} className="flex justify-between py-1.5 text-sm">
                      <span className="text-slate-600">{l.product}</span>
                      <span className="font-mono text-xs text-slate-500">{gbp(l.amount_gbp)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <button onClick={() => viewPdf(inv.id)} data-testid="my-invoice-view" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
                    <ExternalLink className="h-4 w-4" /> View PDF
                  </button>
                  {inv.status === "sent" && (
                    <Link to={`/pay-invoice/${inv.id}`} data-testid="my-invoice-pay" className="inline-flex items-center gap-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg px-4 py-2 transition-all">
                      <CreditCard className="h-4 w-4" /> Pay {gbp(inv.total_gbp)}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
